import { defineComponent, h, nextTick } from 'vue';
import { QTree, QIcon } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import EventBus from '../event-bus.js';

import utils from '../../Shared/utils.jsx';
import methods from '../../Shared/methods';

const BasicTreeNode = defineComponent({
    name: 'basic-tree-node',
    emits: ['finished-render'],
    data: function () {
        return {
            _uid: utils._uid_counter++,
        }
    },
    props: {
        sourceData: {
            type: Object,
            default: () => { }
        },
        nodeTemplate: {
            type: Array,
            default: () => []
        },
        root: {
            type: Object,
            default: null
        },
        parent: {
            type: Object,
            default: null
        }
    },
    methods: {
        generateItems() {
            let returnItems = [];
            this.nodeTemplate.forEach((c, index) => {
                if (!(typeof c === 'object'))
                    return;

                let DynamicControl = utils.getDynamicComponent(h, c);

                if (!c.$objectId) c.$objectId = utils.generateUUID();

                let id = c.$objectId;
                if (index) id += '_' + index;

                returnItems.push(
                    <DynamicControl
                        onFinishedRender={(reference) => this.$emit('finished-render', reference)}
                        key={id}
                        type={c.ControlType}
                        name={c.ControlData.Name ? c.ControlData.Name : ''}
                        root={this.parent.root}
                        parentType="BasicTree"
                        controlData={c.ControlData}
                        controlURL={c.ControlURL}
                        sourceIndex={index}
                        controlName={c.Name}
                        scopeitems={this.parent.scopeitems}
                        controlscope={this.parent.controlscope}
                        cacheControl={c.CacheControl}
                        controlEvents={c.Events}
                    >
                    </DynamicControl>
                );
            });
            return returnItems;
        },
    },
    render() {
        let items = this.generateItems();
        return (
            <div class="column c-BasicTree-node" style="flex-grow: 1;">
                {items}
            </div>
        )
    }
});

export default defineComponent({
    name: 'basic-tree',
    mixins: [BaseComponent],
    components: {
        BasicTreeNode,
        QTree,
        QIcon,
    },
    data() {
        return {
            sourceUrlEval: null,
            data: [],
            sourceRawInterpolatedEval: null,
            expandedNodeExpressionEval: null,
            selectedNodeExpressionEval: null,
            open: [],
            selectedNodeData: [],
            expandedNodeData: [],
            Node: {},
            treeRefId: `basic_tree_${utils.generateUUID()}`,
        }
    },
    watch: {
        sourceUrl: async function() {
            this.data = await utils.api.get(utils.evaluate(this.sourceUrlEval, this));
        }
    },
    computed: {
        sourceUrl() {
            return utils.evaluate(this.sourceUrlEval, this);
        },
        sourceRawInterpolated() {
            return utils.evaluateObject(this.sourceRawInterpolatedEval, this);
        },
        sourceData() {
            let srcdata;
            switch(this.controlData.SourceType) {
                case 'Url':
                case 'Raw':
                    srcdata = this.data;
                    break;
                case 'RawInterpolated':
                    srcdata = this.sourceRawInterpolated;
                    break;
                default:
                    srcdata = [];
                    break;
            }
            return srcdata;
        },
        expandedNodeExpression() {
            if(this.expandedNodeExpressionEval === null && this.controlData.ExpandedNodeExpression)
                this.expandedNodeExpressionEval = utils.compileExpression(this, this.controlData.ExpandedNodeExpression);
            if(this.expandedNodeExpressionEval)
                return utils.evaluate(this.expandedNodeExpressionEval, this);
            return '';
        },
        openNodes() {
            return this.findExpandedNodes(this.sourceData);
        },
        selectedNode() {
            if(this.selectedNodeExpressionEval === null && this.controlData.SelectedNodeExpressionEval)
                this.selectedNodeExpressionEval = utils.compileExpression(this, this.controlData.SelectedNodeExpressionEval)
            if(this.selectedNodeExpressionEval)
                return this.findSelectedNode(this.sourceData);
            return [];
        },
        styles() {
            return {
                ...utils.resolveStyleHints(this.styleHints, this),
                ...this.sizeStyle
            }
        }
    },
    methods: {
        async preRenderComplete() {
            switch(this.controlData.SourceType) {
                case 'Url':
                    this.sourceUrlEval = utils.compile(this, this.controlData.SourceUrl);
                    this.data = await utils.api.get(utils.evaluate(this.sourceUrlEval, this));
                    break;
                case 'Raw': 
                    this.data = this.controlData.SourceRaw;
                    break;
                case 'RawInterpolated':
                    this.sourceRawInterpolatedEval = utils.compileObject(this, this.controlData.SourceRaw);
                    break;
            }
            this.open = this.openNodes;
            this.finishRenderHandler(this);
        },
        ExpandAll() {
            const c = this;
            if (this.treeRefId in this.$refs)
                nextTick(function () {
                    const tree = c.$refs[c.treeRefId];
                    if (tree && tree.expandAll) tree.expandAll();
                });
        },
        CollapseAll() {
            const c = this;
            if (this.treeRefId in this.$refs)
                nextTick(function () {
                    const tree = c.$refs[c.treeRefId];
                    if (tree && tree.collapseAll) tree.collapseAll();
                });
        },
        hasChildren(node) {
            const childArrayName = this.controlData.ChildArrayName || 'children';
            if (Array.isArray(node?.children))
                return node.children.length > 0;
            return Array.isArray(node?.[childArrayName]) && node[childArrayName].length > 0;
        },
        toggleNode(nodeKey, node) {
            if (nodeKey == null || !this.hasChildren(node))
                return;
            const willExpand = !this.open.includes(nodeKey);
            this.open = willExpand
                ? [...this.open, nodeKey]
                : this.open.filter(k => k !== nodeKey);

            // Keep QTree internal state in sync even if slot/key mapping differs.
            const tree = this.$refs[this.treeRefId];
            if (tree && tree.setExpanded)
                tree.setExpanded(nodeKey, willExpand);
        },
        onExpandedUpdate(keys) {
            this.open = keys || [];
        },
        findSelectedNode(nodes) {
            nodes.forEach(node => {
                this.Node = node;
                if(utils.evaluate(this.selectedNodeExpressionEval, this))
                    this.selectedNode = [node.Key];
                if(node[this.controlData.ChildArrayName] && node[this.controlData.ChildArrayName].length > 0)
                    this.findSelectedNode(node[this.controlData.ChildArrayName])
            });
            this.Node = {};
            return;
        },
        findExpandedNodes(nodes) {
            let expandedNodes = new Set();
            this.findExpandedNodesRecursive(nodes, expandedNodes);
            this.Node = {};
            return Array.from(expandedNodes);
        },
        findExpandedNodesRecursive(nodes, expandedNodes) {
            nodes.forEach(node => {
                this.Node = node;
                if(this.expandedNodeExpression){
                    expandedNodes.add(node.Key)
                    this.findAncestorNodes(node, this.sourceData).forEach(item => expandedNodes.add(item));
                }
                // depth first traverse
                if(node[this.controlData.ChildArrayName] && node[this.controlData.ChildArrayName].length > 0)
                    this.findExpandedNodesRecursive(node[this.controlData.ChildArrayName], expandedNodes);
            });
        },
        findAncestorNodes(node, nodes) {
            let ancestorNodes = new Set();
            this.findAncestorNodesRecursive(node, nodes, ancestorNodes);
            return Array.from(ancestorNodes);
        },
        findAncestorNodesRecursive(childNode, nodes, ancestorNodes) {
            nodes.forEach(node => {
                if(node.Key === childNode.Key)
                    return;
                else {
                    if(node[this.controlData.ChildArrayName] && node[this.controlData.ChildArrayName].length > 0) {
                        ancestorNodes.add(node.Key);
                        this.findAncestorNodesRecursive(childNode, node[this.controlData.ChildArrayName], ancestorNodes);
                    }
                    else 
                        return;
                }
            })
        },
    },
    props: {},
    render() {
        if(!this.todisplay)
            return null;
        try {
            const slots = {
                ['default-header']: ({ node, key, expanded }) => {
                    return (
                        <BasicTreeNode
                            onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                            sourceData={node}
                            nodeTemplate={this.controlData.TreeNodeTemplate}
                            root={this.root}
                            parent={this}
                        ></BasicTreeNode>
                    );
                },
            };

            let treeView =
                <QTree
                    ref={this.treeRefId}
                    dense={false}
                    noConnectors
                    class="q-ma-xs"
                    nodes={this.sourceData || []}
                    node-key={this.controlData.NodeIDKey || 'id'}
                    label-key="label"
                    default-expand-all={false}
                    children-key={this.controlData.ChildArrayName || 'children'}
                    expanded={this.open}
                    onUpdate:expanded={this.onExpandedUpdate}
                    v-slots={slots}
                />;

            return(
                <div
                    class={{ 'c-BasicTree': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    v-show={this.isvisible} style={this.styles}>
                    {treeView}
                </div>
            ) 
        }
        catch (e) {
            utils.error('BasicTree Render failed', e);
            return <div>BasicTree Failed to Render {e}</div>;
        }
    }
});